import React, { useState, useEffect, useCallback } from 'react';
import { Button, Steps, Stepper, TextArea } from 'antd-mobile';
import { CheckCircleOutline } from 'antd-mobile-icons';
import { useSnapshot } from 'valtio';
import { appStateProxy } from '@/hooks/useAppState.ts';
import { settingsApi } from '@/API/settingsAPI.ts';
import onboardingAPI from '@/API/onboardingAPI.ts';
import type { Client } from '@spona/rest-api-types';
import './OnboardingWizardMobile.css';

const PLAN_LABEL: Record<string, string> = {
    starter: 'Starter',
    standard: 'Standard',
    pro: 'Pro',
    custom: 'Custom'
};

const OnboardingWizardMobile: React.FC = () => {
    const appStateSnap = useSnapshot(appStateProxy);
    const [currentStep, setCurrentStep] = useState(0);
    const [isCompleting, setIsCompleting] = useState(false);

    const [deadlineTo, setDeadlineTo] = useState<number>(7);
    const [companyInfoText, setCompanyInfoText] = useState<string>('');
    const [orderNoteText, setOrderNoteText] = useState<string>('');
    const [isSavingDeadline, setIsSavingDeadline] = useState(false);

    useEffect(() => {
        if (currentStep === 1) {
            settingsApi.getDefaultDeadline().then(res => {
                if (res.success && res.data) {
                    setDeadlineTo(res.data.to ?? 7);
                }
            }).catch(() => {});
            settingsApi.getCompanyInfo().then(res => {
                if (res.success && res.data) {
                    setCompanyInfoText((res.data as any) as string ?? '');
                }
            }).catch(() => {});
            settingsApi.getOrderNote().then(res => {
                if (res.success && res.data) {
                    setOrderNoteText((res.data as any) as string ?? '');
                }
            }).catch(() => {});
        }
    }, [currentStep]);

    const client = appStateSnap.user?.client;
    const clientObj = typeof client === 'object' && client ? client as Client & { trialEndDate?: string } : null;

    const trialEndDate = clientObj?.trialEndDate
        ? new Date(clientObj.trialEndDate).toLocaleDateString('sr-RS', { day: '2-digit', month: 'long', year: 'numeric' })
        : null;
    const plan = clientObj?.plan ? PLAN_LABEL[clientObj.plan] ?? clientObj.plan : '—';
    const subscriptionStatus = clientObj?.subscriptionStatus;

    const markCompleted = useCallback(async () => {
        setIsCompleting(true);
        try {
            await onboardingAPI.completeOnboarding();
            if (appStateProxy.user) {
                appStateProxy.user.onboardingCompleted = true;
            }
        } catch {
            if (appStateProxy.user) {
                appStateProxy.user.onboardingCompleted = true;
            }
        } finally {
            setIsCompleting(false);
        }
    }, []);

    const handleNext = useCallback(async () => {
        if (currentStep === 1) {
            setIsSavingDeadline(true);
            try {
                await Promise.all([
                    settingsApi.updateDefaultDeadline({ to: deadlineTo }),
                    settingsApi.updateCompanyInfo({ text: companyInfoText } as any),
                    settingsApi.updateOrderNote({ text: orderNoteText } as any),
                ]);
            } catch {
                // Non-blocking — proceed anyway
            } finally {
                setIsSavingDeadline(false);
            }
        }
        if (currentStep < 3) {
            setCurrentStep(s => s + 1);
        }
    }, [currentStep, deadlineTo, companyInfoText, orderNoteText]);

    const handleBack = useCallback(() => {
        setCurrentStep(s => s - 1);
    }, []);

    // ---- Step content renderers ----

    const renderWelcome = () => (
        <>
            <div className="spona-onboarding-m-step-title">Dobrodošli u Sponu!</div>
            <div className="spona-onboarding-m-step-desc">
                Vaš nalog je uspešno kreiran. Imate <strong>30 dana besplatnog probnog perioda</strong> da isprobate sve funkcionalnosti.
            </div>

            {subscriptionStatus === 'trial' && (
                <div className="spona-onboarding-m-trial-card">
                    <div className="spona-onboarding-m-trial-row">
                        <span className="spona-onboarding-m-trial-label">Plan</span>
                        <span className="spona-onboarding-m-trial-value">{plan}</span>
                    </div>
                    <div className="spona-onboarding-m-trial-row">
                        <span className="spona-onboarding-m-trial-label">Status</span>
                        <span className="spona-onboarding-m-trial-value">Probni period</span>
                    </div>
                    {trialEndDate && (
                        <div className="spona-onboarding-m-trial-row">
                            <span className="spona-onboarding-m-trial-label">Ističe</span>
                            <span className="spona-onboarding-m-trial-value">{trialEndDate}</span>
                        </div>
                    )}
                </div>
            )}

            <ul className="spona-onboarding-m-feature-list">
                <li>
                    <span className="spona-onboarding-m-feature-icon">✓</span>
                    Upravljanje narudžbinama i kupcima
                </li>
                <li>
                    <span className="spona-onboarding-m-feature-icon">✓</span>
                    Katalog proizvoda i varijante proizvoda
                </li>
                <li>
                    <span className="spona-onboarding-m-feature-icon">✓</span>
                    Generisanje PDF predračuna i radnih naloga
                </li>
                <li>
                    <span className="spona-onboarding-m-feature-icon">✓</span>
                    Upravljanje timom i dozvolama
                </li>
            </ul>
        </>
    );

    const renderSettings = () => (
        <>
            <div className="spona-onboarding-m-step-title">Osnovna podešavanja</div>
            <div className="spona-onboarding-m-step-desc">
                Podesite podrazumevane vrednosti za narudžbine. Sve ovo možete promeniti iz Podešavanja.
            </div>

            <div className="spona-onboarding-m-field-label">
                Podrazumevani rok isporuke
            </div>
            <div className="spona-onboarding-m-deadline-row">
                <div className="spona-onboarding-m-deadline-item">
                    <span className="spona-onboarding-m-deadline-label">Do</span>
                    <Stepper
                        min={0}
                        max={365}
                        value={deadlineTo}
                        onChange={v => setDeadlineTo(v)}
                    />
                </div>
            </div>
            <div className="spona-onboarding-m-deadline-unit">dana</div>

            <div className="spona-onboarding-m-field-label" style={{ marginTop: '1.25rem' }}>
                Informacije o firmi
                <span className="spona-onboarding-m-field-hint">Prikazuje se na predračunima i radnim nalozima</span>
            </div>
            <TextArea
                rows={3}
                value={companyInfoText}
                onChange={val => setCompanyInfoText(val)}
                placeholder="npr. Naziv firme, adresa, PIB, telefon..."
                className="spona-onboarding-m-textarea"
            />

            <div className="spona-onboarding-m-field-label" style={{ marginTop: '1.25rem' }}>
                Podrazumevana napomena
                <span className="spona-onboarding-m-field-hint">Dodaje se na svaki novi nalog automatski</span>
            </div>
            <TextArea
                rows={2}
                value={orderNoteText}
                onChange={val => setOrderNoteText(val)}
                placeholder="npr. Hvala na poverenju!"
                className="spona-onboarding-m-textarea"
            />
        </>
    );

    const renderTeam = () => (
        <>
            <div className="spona-onboarding-m-step-title">Pozovite tim</div>
            <div className="spona-onboarding-m-step-desc">
                Koristite Sponu sami ili sa celim timom.
            </div>

            <div className="spona-onboarding-m-info-box">
                <p style={{ margin: '0 0 0.75rem' }}>
                    Korisnikе možete dodati kroz <strong>Podešavanja → Korisnici</strong>.
                    Za svakog korisnika možete podesiti ulogu i tačne dozvole (admin, prodavac, radnik u produkciji...)
                </p>
                <p style={{ margin: 0 }}>
                    Grupe vam pomažu da organizujete tim i ograničite vidljivost narudžbina po sektorima ili prodajnim kanalima.
                </p>
            </div>
        </>
    );

    const renderDone = () => (
        <>
            <div className="spona-onboarding-m-done-icon">
                <CheckCircleOutline />
            </div>
            <div className="spona-onboarding-m-step-title" style={{ textAlign: 'center' }}>Sve je spremno!</div>
            <div className="spona-onboarding-m-step-desc" style={{ textAlign: 'center' }}>
                Vaš nalog je podešen i možete ga koristiti odmah. Kliknite <strong>Završi</strong> da počnete.
            </div>
        </>
    );

    const stepContent = [renderWelcome, renderSettings, renderTeam, renderDone];

    return (
        <div className="spona-onboarding-m-overlay">
            <div className="spona-onboarding-m-container">

                {/* Header */}
                <div className="spona-onboarding-m-header">
                    <img
                        className="spona-onboarding-m-logo"
                        src="/logos/spona-default-logo-text.png"
                        alt="Spona"
                    />
                    <Steps current={currentStep}>
                        <Steps.Step title="Dobrodošli" />
                        <Steps.Step title="Podešavanja" />
                        <Steps.Step title="Tim" />
                        <Steps.Step title="Gotovo" />
                    </Steps>
                </div>

                {/* Body */}
                <div className="spona-onboarding-m-body">
                    {stepContent[currentStep]()}
                </div>

                {/* Footer */}
                <div className="spona-onboarding-m-footer">
                    <button
                        className="spona-onboarding-m-skip"
                        onClick={() => markCompleted()}
                        disabled={isCompleting}
                    >
                        Preskoči
                    </button>

                    <div className="spona-onboarding-m-footer-actions">
                        {currentStep > 0 && (
                            <Button
                                onClick={handleBack}
                                disabled={isCompleting || isSavingDeadline}
                                className="spona-onboarding-m-btn-back"
                            >
                                Nazad
                            </Button>
                        )}

                        {currentStep < 3 ? (
                            <Button
                                color="primary"
                                fill="solid"
                                onClick={handleNext}
                                loading={isSavingDeadline}
                                disabled={isCompleting}
                                className="spona-onboarding-m-btn-next"
                            >
                                {currentStep === 0 ? 'Počni' : 'Dalje'}
                            </Button>
                        ) : (
                            <Button
                                color="primary"
                                fill="solid"
                                onClick={() => markCompleted()}
                                loading={isCompleting}
                                className="spona-onboarding-m-btn-next"
                            >
                                Završi
                            </Button>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default OnboardingWizardMobile;
